import { useEffect, useState } from "react";
import { supabase } from "@vinoga/db";
import { Mail } from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useT } from "@/lib/i18n";

// "Jaunumu abonenti" — the internal newsletter signup table (owner
// 2026-09-25). Source of truth lives in our DB; the ESP (Brevo — parked —
// or Resend later) only mirrors it.
type Row = {
  id: string;
  email: string;
  first_name: string | null;
  locale: string | null;
  source: string;
  created_at: string;
  unsubscribed_at: string | null;
};

const PAGE = 50;

export function NewsletterSubscribers() {
  const t = useT();
  const [rows, setRows] = useState<Row[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      let q = supabase
        .from("newsletter_subscribers")
        .select("*", { count: "exact" })
        .order("created_at", { ascending: false })
        .range(page * PAGE, page * PAGE + PAGE - 1);
      if (search.trim()) q = q.ilike("email", `%${search.trim()}%`);
      const { data, count } = await q;
      if (cancelled) return;
      setRows((data ?? []) as Row[]);
      setTotal(count ?? 0);
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [page, search]);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 font-display text-xl font-bold text-foreground">
            <Mail size={20} className="text-muted-foreground" />
            {t("newsletter.title", "Jaunumu abonenti")}
          </h1>
          <p className="font-body text-sm text-muted-foreground">
            {t("newsletter.subtitle", "Pieteikšanās jaunumu saņemšanai no veikala.")}{" "}
            {total} {t("newsletter.total", "kopā")}
          </p>
        </div>
        <input
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(0);
          }}
          placeholder={t("newsletter.searchPlaceholder", "Meklēt pēc e-pasta…")}
          className="w-64 rounded-lg border border-border bg-card px-3 py-2 font-body text-sm text-foreground outline-none focus:border-primary"
        />
      </div>

      <div className="rounded-lg border border-border bg-card">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t("newsletter.colEmail", "E-pasts")}</TableHead>
              <TableHead>{t("newsletter.colName", "Vārds")}</TableHead>
              <TableHead>{t("newsletter.colLocale", "Valoda")}</TableHead>
              <TableHead>{t("newsletter.colSource", "Avots")}</TableHead>
              <TableHead>{t("newsletter.colDate", "Pieteicās")}</TableHead>
              <TableHead>{t("newsletter.colStatus", "Statuss")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((r) => (
              <TableRow key={r.id}>
                <TableCell className="font-body text-sm font-medium">{r.email}</TableCell>
                <TableCell className="font-body text-sm text-muted-foreground">
                  {r.first_name ?? "—"}
                </TableCell>
                <TableCell className="font-body text-sm text-muted-foreground">
                  {(r.locale ?? "lv").toUpperCase()}
                </TableCell>
                <TableCell className="font-body text-sm text-muted-foreground">{r.source}</TableCell>
                <TableCell className="font-body text-sm text-muted-foreground">
                  {new Date(r.created_at).toLocaleDateString("lv-LV")}
                </TableCell>
                <TableCell>
                  {r.unsubscribed_at ? (
                    <span className="rounded bg-muted px-2 py-0.5 font-body text-xs text-muted-foreground">
                      {t("newsletter.unsubscribed", "atteicies")}
                    </span>
                  ) : (
                    <span className="rounded bg-emerald-500/15 px-2 py-0.5 font-body text-xs font-medium text-emerald-700">
                      {t("newsletter.active", "aktīvs")}
                    </span>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {!loading && rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} className="py-8 text-center font-body text-sm text-muted-foreground">
                  {t("newsletter.empty", "Abonentu vēl nav.")}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {total > PAGE && (
        <div className="flex items-center justify-center gap-3 font-body text-sm">
          <button
            disabled={page === 0}
            onClick={() => setPage((p) => p - 1)}
            className="rounded border border-border px-3 py-1.5 disabled:opacity-40"
          >
            ←
          </button>
          <span className="text-muted-foreground">
            {page + 1} / {Math.ceil(total / PAGE)}
          </span>
          <button
            disabled={(page + 1) * PAGE >= total}
            onClick={() => setPage((p) => p + 1)}
            className="rounded border border-border px-3 py-1.5 disabled:opacity-40"
          >
            →
          </button>
        </div>
      )}
    </div>
  );
}
